import React, {
    useState,
    useContext,
    useEffect,
    useRef,
    useMemo,
    forwardRef,
} from 'react';
import {
    useTable,
    useSortBy,
    useRowSelect,
    useRowState,
    usePagination,
    useFlexLayout,
    useResizeColumns,
} from 'react-table';
import uniqueId from 'lodash.uniqueid';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    StyledTableRow,
    StyledTable,
    StyledCheckbox,
    StyledTableBody,
    StyledTableHead,
    StyledTableHeadRow,
    StyledColumnHeader,
    TableWrapper,
    TableHeaderRow,
    TableRowWrapper,
    TableCellWrapper,
} from '../../../styles/common';
import { HelpDeskDispatchContext } from '../../../context/HelpDeskContext';
import SortIcon from '../../shared/SortIcon';
import TablePagination from '../../shared/TablePagination';
import TableSettings from '../../shared/TableSettings';
import { renderCell } from '../../../utils/generic';
import { usePrevious } from '../../../utils/hooks';
import {
    readWidths,
    writeWidths,
    clearWidths,
    hasCustomWidths,
} from '../../../utils/columnWidthStorage';
import { useAdaptiveColumnLayout } from '../../../utils/useAdaptiveColumnLayout';
import ResizeHandle from './ResizeHandle';
import ResetColumnsControl from './ResetColumnsControl';
import TruncatedTableCell from './TruncatedTableCell';
import { useColumnResizeActions } from './useColumnResizeActions';
import {
    DEFAULT_COLUMN_SETTINGS,
    MIN_ADAPTIVE_COLUMN_WIDTH,
    MAX_NATURAL_COLUMN_WIDTH,
    COLLAPSE_BREAKPOINT,
    NON_TRUNCATING_COLUMNS,
    clampWidths,
} from './constants';
import {
    StackedTableRow,
    StackedTableCell,
    StackedCellLabel,
    StackedCellValue,
} from './styles';

const TableSettingsWrapper = styled.div`
    text-align: right;
`;

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <StyledCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
});

function processDeviceType(data, t) {
    return data.map((row) => {
        const label = `mappingsLabels$deviceSubTypes$${row.deviceType}$label`;
        return { ...row, deviceType: t(label) ? t(label) : row.deviceType };
    });
}

const NON_RESIZABLE = new Set(['selection', 'expander']);

// Only strip width/maxWidth from setColumnStyle when the user has explicitly resized
// that column. Otherwise setColumnStyle works exactly as before.
const safeColumnStyle = (setColumnStyleFn, column, resizedWidths) => {
    const result = setColumnStyleFn(column);
    if (!result?.style || !resizedWidths?.[column.id]) return result || {};
    const { width, maxWidth, ...rest } = result.style; // eslint-disable-line no-unused-vars
    return { ...result, style: rest };
};

const TableCore = ({
    tableType = '',
    tableId = '',
    hideTableHeader = false,
    data = [{}],
    columnsData = [{}],
    columnsName = [''],
    hideCheckboxes = false,
    hidePagination = false,
    selectedColumnsKey = '',
    onSelectedRow = () => { },
    onColumnsChange = () => { },
    getTableHeader = null,
    defaultColumns,
    defaultColumnSettings: defaultColumn = DEFAULT_COLUMN_SETTINGS,
    enableLocalStorage = true,
    disableSkipPaging = false,
    lazyFetchMode = false,
    lazyFetchCanNext = false,
    lazyFetchPageNumber = null,
    rowDetails = null,
    lazyFetchPreviousPage = () => { },
    lazyFetchNextPage = () => { },
    lazyFetchPageSize = () => { },
    lazyFetchSetPageSize = () => { },
    lazyFetchPageCount = () => { },
    lazyFetchResultsCount = () => { },
    lazyFetchSort = () => { },
    hideColumn = [],
    setColumnStyle = () => { },
    onPageChange = () => { },
    minColumnWidth = MIN_ADAPTIVE_COLUMN_WIDTH,
}) => {
    const [isCheckAllClicked, setIsCheckAllClicked] = useState(false);
    const dispatch = useContext(HelpDeskDispatchContext);
    const { t } = useTranslation();
    processDeviceType(data, t);
    const sortByLocalStorageKey = `${selectedColumnsKey}SortBy`;

    // Column width persistence
    const [hasCustom, setHasCustom] = useState(
        () => tableId && hasCustomWidths(tableId),
    );
    const savedWidths = useMemo(
        // Restored widths are clamped to the shared minimum so a stale or
        // hand-edited storage entry cannot produce a narrower column than a
        // drag would allow (PM-18398: the min applies to persistence too).
        () => (tableId ? clampWidths(readWidths(tableId)) : {}),
        // Only read on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Container ref for adaptive layout measurement
    const containerRef = useRef(null);

    // Adaptive widths derived from the container size AND the rendered content
    // (used when no saved width exists for a column). Below the breakpoint the
    // hook reports isCollapsed and each row is stacked instead of laid out
    // side by side, so the table never scrolls sideways.
    const { columnWidths: adaptiveWidths, isCollapsed } = useAdaptiveColumnLayout({
        containerRef,
        columns: columnsData,
        data,
        minColumnWidth: defaultColumn?.width || minColumnWidth,
        maxNaturalWidth: MAX_NATURAL_COLUMN_WIDTH,
        collapseBelow: COLLAPSE_BREAKPOINT,
    });

    // Merge: adaptive widths as column.width; saved widths go into initialState.columnResizing
    const enrichedColumns = useMemo(() => columnsData.map((col) => {
        const id = col.accessor || col.id;
        const adaptive = adaptiveWidths[id];
        return {
            ...col,
            ...(adaptive ? { width: adaptive } : {}),
        };
    }), [columnsData, adaptiveWidths]);

    const {
        getTableProps,
        prepareRow,
        headers,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { selectedRowIds, pageSize, pageIndex, sortBy, columnResizing },
        selectedFlatRows,
        resetResizing,
        dispatch: tableDispatch,
    } = useTable(
        {
            columns: enrichedColumns,
            data,
            initialState: {
                pageIndex: 0,
                hiddenColumns: hideColumn,
                sortBy: !!localStorage.getItem(sortByLocalStorageKey)
                    ? JSON.parse(localStorage.getItem(sortByLocalStorageKey))
                    : [],
                columnResizing: {
                    columnWidths: savedWidths,
                },
            },
            autoResetSelectRows: false,
            autoResetPage: false,
            autoResetResize: false,
            manualSortBy: lazyFetchMode,
            defaultColumn,
        },
        useFlexLayout,
        useResizeColumns,
        useSortBy,
        usePagination,
        useRowSelect,
        useRowState,
        ({ columns }) => {
            if (!hideCheckboxes && columns) {
                columns.push((columns) => [
                    {
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }) => (
                            <IndeterminateCheckbox
                                onClick={() => setIsCheckAllClicked(true)}
                                {...getToggleAllRowsSelectedProps()}
                            />
                        ),
                        Cell: ({ row: { getToggleRowSelectedProps } }) => (
                            <IndeterminateCheckbox
                                onClick={() => setIsCheckAllClicked(false)}
                                {...getToggleRowSelectedProps()}
                            />
                        ),
                    },
                    ...columns,
                ]);
            }
        },
    );

    // Save widths to localStorage after drag ends.
    // columnDoneResizing preserves the same columnWidths reference, so we watch
    // columnWidths directly — it gets a new reference on every drag movement.
    // Debouncing means we only write 300ms after the last pixel change (i.e. on drag end).
    const saveTimerRef = useRef(null);
    const { columnWidths } = columnResizing;
    useEffect(() => {
        clearTimeout(saveTimerRef.current);
        if (tableId && columnWidths && Object.keys(columnWidths).length > 0) {
            saveTimerRef.current = setTimeout(() => {
                writeWidths(tableId, columnWidths);
                setHasCustom(true);
            }, 300);
        }
        return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnWidths, tableId]);

    const handleResetColumns = () => {
        if (tableId) {
            clearWidths(tableId);
        }
        resetResizing();
        setHasCustom(false);
    };

    // Shared with ReportsTableWithData so both tables auto-fit and keyboard-resize
    // identically (PM-18153: consistent across all main tables).
    const { handleAutoFit, handleKeyboardResize } = useColumnResizeActions({ rows, tableDispatch, tableType, containerRef });

    const prevSelectedRowIds = usePrevious(selectedRowIds);
    const hasRowSelected = !isEmpty(prevSelectedRowIds) || !isEmpty(selectedRowIds);
    const pageIndexRef = useRef(null);
    const selectedRowIdsRef = useRef(null);
    const lazyFetchPageNumberRef = useRef(null);
    const sortByRef = useRef(null);

    useEffect(() => {
        if (pageIndex !== pageIndexRef.current) {
            pageIndexRef.current = pageIndex;
            onPageChange(pageIndex);
        }
    }, [pageIndex, onPageChange]);

    useEffect(() => {
        if (!!sortBy) {
            localStorage.setItem(sortByLocalStorageKey, JSON.stringify(sortBy));
        }
    }, [sortBy, sortByLocalStorageKey]);

    useEffect(() => {
        if (selectedRowIdsRef.current !== selectedRowIds) {
            selectedRowIdsRef.current = selectedRowIds;
            const selectedIds = Object.keys(selectedRowIds);
            const selectedRowsData = !isEmpty(selectedIds)
                ? selectedIds.map((x) => data[x]).filter((x) => x !== null && x !== undefined)
                : [];
            if (hasRowSelected) {
                onSelectedRow(selectedRowsData);
            }
        }
        // In Lazy mode, page might be updated before the data is fetch.
        // This call force the page to be updated, after new data is updated.
        lazyUpdatePageNumber();
    }, [selectedRowIds, data, hasRowSelected, onSelectedRow]);

    useEffect(() => {
        if (isCheckAllClicked) {
            const pageRows = selectedFlatRows.filter((a) =>
                page.some((b) => a.id === b.id),
            );

            rows.forEach((row) => {
                prepareRow(row);
                if (!pageRows.includes(row) || !isEmpty(prevSelectedRowIds)) {
                    row.toggleRowSelected(false);
                }
            });
            setIsCheckAllClicked(false);
        }
    }, [page, selectedFlatRows, isCheckAllClicked, rows, prepareRow, prevSelectedRowIds]);

    const onPageSizeChange = (value) => {
        if (lazyFetchMode) {
            value && lazyFetchSetPageSize(value);
        } else {
            value && setPageSize(value);
        }
    };

    useEffect(() => {
        if (lazyFetchPageNumberRef.current !== lazyFetchPageNumber) {
            lazyUpdatePageNumber();
        }
    }, [lazyFetchPageNumber, lazyFetchMode, lazyFetchPageSize, gotoPage, setPageSize]);

    const lazyUpdatePageNumber = () => {
        if (!lazyFetchMode) {
            return;
        }
        lazyFetchPageNumberRef.current = lazyFetchPageNumber;
        gotoPage(lazyFetchPageNumber);
        setPageSize(lazyFetchPageSize);
    };

    const totalCount = lazyFetchMode ? lazyFetchResultsCount : data.length;

    const DefaultTableSettings = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <ResetColumnsControl visible={hasCustom} onReset={handleResetColumns} />
            <TableSettings
                onPageSizeChange={onPageSizeChange}
                onColumnsChange={onColumnsChange}
                selectedColumnsKey={selectedColumnsKey}
                columnsName={columnsName}
                pageSize={pageSize}
                hidePagination={hidePagination}
                defaultColumns={defaultColumns}
                enableLocalStorage={enableLocalStorage}
            />
        </div>
    );
    const tableHeader = () => {
        // User pass getTableHeader as a function to set custom header.
        if (!!getTableHeader && typeof getTableHeader === 'function') {
            return getTableHeader(DefaultTableSettings);
        }
        // Otherwise return default table settings.
        return DefaultTableSettings;
    };

    useEffect(() => {
        if (lazyFetchMode && sortByRef.current !== sortBy) {
            sortByRef.current = sortBy;
            lazyFetchSort(headers);
        }
    }, [sortBy, lazyFetchSort, lazyFetchMode, headers]);

    return (
        <>
            <TableHeaderRow $hideTableHeader={hideTableHeader}>
                {tableHeader()}
            </TableHeaderRow>

            <TableWrapper ref={containerRef}>
                <StyledTable hover borderless {...getTableProps()}>
                    {!isCollapsed && (
                        <StyledTableHead>
                            <StyledTableHeadRow>
                                {headers.map((column) => {
                                    if (!column.isVisible) return null;
                                    const headerProps = column.getHeaderProps({
                                        ...column.getSortByToggleProps(),
                                        ...safeColumnStyle(setColumnStyle, column, columnWidths),
                                    });
                                    return (
                                        <div
                                            key={uniqueId(`${tableType}Header_`)}
                                            id={`${tableType}Header_${column.id}`}
                                            name={column.id}
                                            {...headerProps}
                                            style={{
                                                ...headerProps.style,
                                                position: 'relative',
                                                paddingRight: NON_RESIZABLE.has(column.id) ? undefined : '12px',
                                            }}
                                        >
                                            <StyledColumnHeader>
                                                {column.render('Header')}
                                            </StyledColumnHeader>
                                            <SortIcon
                                                isSorted={column.isSorted}
                                                isSortedDesc={column.isSortedDesc}
                                                hide={
                                                    column.id === 'action' ||
                                                column.id === 'selection' ||
                                                column.id === 'expander' ||
                                                column.id === 'logo' ||
                                                column.id === 'type'
                                                }
                                            />
                                            {!NON_RESIZABLE.has(column.id) && (
                                                <ResizeHandle
                                                    column={column}
                                                    onDoubleClick={handleAutoFit}
                                                    onKeyboardResize={handleKeyboardResize}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </StyledTableHeadRow>
                        </StyledTableHead>
                    )}
                    <StyledTableBody>
                        {page.map((row) => {
                            prepareRow(row);
                            const rowProps = row.getRowProps();
                            // A stacked row lays out vertically, so the flex-row
                            // geometry from react-table must not be applied to it.
                            const RowElement = isCollapsed ? StackedTableRow : StyledTableRow;
                            return (
                                <TableRowWrapper key={row.index}>
                                    <RowElement
                                        key={uniqueId(`${tableType}Row_`)}
                                        id={`${tableType}Row_${row.id}`}
                                        onClick={() =>
                                            dispatch({
                                                type: 'SET_HELPDESK_USER',
                                                payload: row.original.user,
                                            })
                                        }
                                        {...rowProps}
                                        style={isCollapsed ? undefined : rowProps.style}
                                    >
                                        {row.cells.map((cell) => {
                                            const cellId = `${tableType}Cell_${cell.column.id}-${row.id}`;
                                            const cellKey = uniqueId(`${tableType}Cell_`);
                                            // Interactive cells render plain: truncating them clips
                                            // controls out of reach rather than shortening text (PM-21741).
                                            const cellBody = NON_TRUNCATING_COLUMNS.has(cell.column.id)
                                                ? renderCell(cell)
                                                : (
                                                    <TruncatedTableCell>
                                                        {renderCell(cell)}
                                                    </TruncatedTableCell>
                                                );

                                            if (isCollapsed) {
                                                // Only textual headers make sense as a label; the
                                                // selection and expander headers render controls.
                                                const label = typeof cell.column.Header === 'string'
                                                    ? cell.column.Header
                                                    : null;
                                                return (
                                                    <StackedTableCell key={cellKey} id={cellId}>
                                                        {label && (
                                                            <StackedCellLabel>{label}</StackedCellLabel>
                                                        )}
                                                        <StackedCellValue>
                                                            {cellBody}
                                                        </StackedCellValue>
                                                    </StackedTableCell>
                                                );
                                            }

                                            return (
                                                <TableCellWrapper
                                                    key={cellKey}
                                                    id={cellId}
                                                    {...cell.getCellProps({
                                                        ...safeColumnStyle(setColumnStyle, cell.column, columnWidths),
                                                    })}
                                                >
                                                    {cellBody}
                                                </TableCellWrapper>
                                            );
                                        })}
                                    </RowElement>

                                    {/* row details drop down area */}
                                    {!!rowDetails && rowDetails(row)}
                                </TableRowWrapper>
                            );
                        })}
                    </StyledTableBody>
                </StyledTable>
            </TableWrapper>
            <TableSettingsWrapper>
                {!hidePagination && totalCount > pageSize && (
                    <>
                        <TablePagination
                            gotoPage={!disableSkipPaging && gotoPage}
                            previousPage={
                                lazyFetchMode
                                    ? lazyFetchPreviousPage
                                    : previousPage
                            }
                            nextPage={
                                lazyFetchMode ? lazyFetchNextPage : nextPage
                            }
                            canPreviousPage={canPreviousPage}
                            canNextPage={
                                lazyFetchMode ? lazyFetchCanNext : canNextPage
                            }
                            pageIndex={pageIndex}
                            pageCount={
                                lazyFetchMode ? lazyFetchPageCount : pageCount
                            }
                            totalCount={totalCount}
                            disableSkipPaging={disableSkipPaging}
                        />
                    </>
                )}
            </TableSettingsWrapper>
        </>
    );
};

export default TableCore;
